import { styled } from "@mui/material/styles";
import { keyframes } from "@emotion/react";

const mutePulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0.35); transform: scale(1); }
  50% { box-shadow: 0 0 0 8px rgba(255,255,255,0); transform: scale(1.06); }
`;

// ── SkillsAcademyRoot ────────────────────────────────────────────────────────────
export const SkillsAcademyRoot = styled("div")(() => ({}));

// ── primitives ─────────────────────────────────────────────────────────────────

export const StyledPill = styled("span")(({ $bg, $fg, $bd, $isSmall }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  height: $isSmall ? 22 : 26,
  padding: $isSmall ? "2px 8px" : "4px 10px",
  background: $bg,
  color: $fg,
  border: `1px solid ${$bd}`,
  borderRadius: 999,
  fontSize: $isSmall ? 12 : 13,
  fontWeight: 500,
  whiteSpace: "nowrap",
  fontFamily: "Poppins, sans-serif",
}));

export const StyledGuruAvatar = styled("div")(({ $size, $src, $ring }) => ({
  width: $size,
  height: $size,
  borderRadius: "50%",
  flexShrink: 0,
  background: "#211F45",
  backgroundImage: $src ? `url(${$src})` : undefined,
  backgroundSize: "cover",
  backgroundPosition: "center",
  boxShadow: $ring
    ? `0 0 0 2px ${$ring},0 0 0 4px rgba(255,255,255,0.05)`
    : undefined,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#FEFEFE",
  fontWeight: 600,
  fontSize: $size * 0.4,
  fontFamily: "Poppins, sans-serif",
}));

export const StarRowRoot = styled("span")({
  display: "inline-flex",
  gap: 2,
  alignItems: "center",
});

export const StarItemWrap = styled("span")({
  position: "relative",
  display: "inline-block",
  lineHeight: 0,
});

export const StarFillOverlay = styled("span")(({ $half }) => ({
  position: "absolute",
  inset: 0,
  width: $half ? "50%" : "100%",
  overflow: "hidden",
  lineHeight: 0,
}));

export const StyledLiveDot = styled("span")({
  width: 6,
  height: 6,
  borderRadius: "50%",
  background: "#FF4D6D",
  display: "inline-block",
  animation: "mp-blink 1.4s ease-in-out infinite",
});

export const StyledIconBtn = styled("button")({
  width: 36,
  height: 36,
  borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.08)",
  background: "rgba(255,255,255,0.04)",
  color: "#D1C7FF",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "all 150ms",
  fontFamily: "Poppins,sans-serif",
});

export const DropdownRoot = styled("div")({
  position: "relative",
});

export const DropdownTrigger = styled("button")(
  ({ $open, $trigHover, $iconOnly }) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: $iconOnly ? "0" : "0 14px",
    height: 50,
    width: $iconOnly ? 44 : undefined,
    minWidth: $iconOnly ? undefined : 180,
    borderRadius: 10,
    background: $open ? "rgba(150,46,255,0.08)" : "rgba(3,0,57,0.9)",
    border: `1px solid ${$open ? "rgba(150,46,255,0.6)" : $trigHover ? "rgba(255,255,255,0.28)" : "rgba(255,255,255,0.10)"}`,
    boxShadow: $open ? "0 0 0 3px rgba(150,46,255,0.14)" : "none",
    color: "#FEFEFE",
    fontSize: 14,
    cursor: "pointer",
    fontFamily: "Poppins,sans-serif",
    transition: "border-color 150ms, background 150ms, box-shadow 150ms",
  }),
);

export const DropdownValueSpan = styled("span")(({ $isDefault }) => ({
  flex: 1,
  textAlign: "left",
  color: $isDefault ? "#A87EFF" : "#FEFEFE",
}));

export const DropdownChevron = styled("span")(({ $open }) => ({
  opacity: 0.6,
  marginLeft: "auto",
  display: "inline-flex",
  transform: $open ? "rotate(180deg)" : undefined,
  transition: "transform 150ms",
}));

export const DropdownBackdrop = styled("div")({
  position: "fixed",
  inset: 0,
  zIndex: 50,
});

export const DropdownMenu = styled("div")({
  position: "absolute",
  top: "calc(100% + 6px)",
  left: 0,
  minWidth: "100%",
  background: "#0A0754",
  border: "1px solid rgba(150,46,255,0.25)",
  borderRadius: 10,
  padding: 6,
  zIndex: 51,
  boxShadow: "0 20px 40px rgba(0,0,0,0.5),0 0 0 1px rgba(150,46,255,0.08)",
});

export const DropdownOption = styled("button")(
  ({ $isSelected, $isHovered }) => ({
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    background: $isSelected
      ? "rgba(150,46,255,0.18)"
      : $isHovered
        ? "rgba(255,255,255,0.05)"
        : "none",
    border: "none",
    color: $isSelected || $isHovered ? "#FEFEFE" : "#D1C7FF",
    fontSize: 14,
    cursor: "pointer",
    borderRadius: 6,
    textAlign: "left",
    fontFamily: "Poppins,sans-serif",
    transition: "background 80ms",
  }),
);

export const DropdownOptionSpacer = styled("span")({
  width: 14,
  display: "inline-block",
  flexShrink: 0,
});

// ── TabBar ─────────────────────────────────────────────────────────────────────

export const TabBarRoot = styled("div")({
  display: "flex",
  borderBottom: "1px solid rgba(255,255,255,0.08)",
  marginBottom: 32,
  overflowX: "auto",
});

export const TabBarButton = styled("button")(({ $isActive }) => ({
  padding: "14px 22px",
  background: "none",
  border: "none",
  borderBottom: $isActive ? "2px solid #01ECFF" : "2px solid transparent",
  color: $isActive ? "#FEFEFE" : "#A87EFF",
  fontSize: 14,
  fontWeight: $isActive ? 600 : 400,
  cursor: "pointer",
  fontFamily: "Poppins,sans-serif",
  transition: "all 150ms",
  whiteSpace: "nowrap",
  marginBottom: -1,
}));

// ── TopicAccordion ─────────────────────────────────────────────────────────────

export const AccordionRoot = styled("div")({
  display: "flex",
  flexDirection: "column",
});

export const AccordionItem = styled("div")({
  borderBottom: "1px solid rgba(255,255,255,0.07)",
});

export const AccordionHeader = styled("button")({
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: 16,
  padding: "18px 0",
  background: "none",
  border: "none",
  cursor: "pointer",
  fontFamily: "Poppins,sans-serif",
  textAlign: "left",
  "@media(max-width:639px)": {
    alignItems: "flex-start",
  },
});

export const AccordionIconBox = styled("div")(({ $isOpen }) => ({
  width: 38,
  height: 38,
  borderRadius: 10,
  flexShrink: 0,
  background: $isOpen ? "rgba(1,236,255,0.12)" : "rgba(255,255,255,0.04)",
  border: `1px solid ${$isOpen ? "rgba(1,236,255,0.3)" : "rgba(255,255,255,0.08)"}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 200ms",
  "@media(max-width:639px)": {
    marginTop: 2,
  },
}));

export const AccordionTitleFlex = styled("div")({
  flex: 1,
  minWidth: 0,
});

export const AccordionTitle = styled("span")(({ $isOpen }) => ({
  fontSize: 15,
  fontWeight: 600,
  color: $isOpen ? "#FEFEFE" : "#D1C7FF",
  transition: "color 150ms",
}));

export const AccordionSubPills = styled("div")({
  display: "flex",
  gap: 6,
  marginTop: 4,
  flexWrap: "wrap",
});

export const AccordionBody = styled("div")({
  paddingBottom: 24,
  paddingLeft: 54,
  animation: "mp-fade-in 150ms ease-out",
});

export const AccordionDesc = styled("p")({
  margin: "0 0 20px",
  fontSize: 14,
  color: "#D1C7FF",
  lineHeight: 1.6,
});

export const AccordionSessionList = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 8,
  marginBottom: 18,
});

export const AccordionSessionRow = styled("div")(({
  $isSim,
  $isLocked,
  $status,
}) => {
  const isCompleted = $status === "completed";
  const isNext = $status === "next";
  const isInteractive = isCompleted || isNext;

  const accentAlpha = (a) =>
    $isSim ? `rgba(150,46,255,${a})` : `rgba(1,236,255,${a})`;

  let bg, borderColor, hoverBg, hoverBorder;
  if ($isLocked) {
    bg = "rgba(255,255,255,0.02)";
    borderColor = "rgba(255,255,255,0.06)";
  } else if (isCompleted) {
    bg = "rgba(23,178,106,0.10)";
    borderColor = "rgba(23,178,106,0.32)";
    hoverBg = "rgba(23,178,106,0.16)";
    hoverBorder = "rgba(23,178,106,0.50)";
  } else {
    bg = accentAlpha(0.04);
    borderColor = accentAlpha($isSim ? 0.15 : 0.12);
    hoverBg = "rgba(3,0,57,0.70)";
    hoverBorder = accentAlpha(0.45);
  }

  return {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 14px",
    borderRadius: 10,
    background: bg,
    border: `1px solid ${borderColor}`,
    opacity: $isLocked ? 0.5 : 1,
    cursor: isInteractive ? "pointer" : "default",
    transition:
      "transform 150ms ease, border-color 150ms ease, background 150ms ease",
    ...(isInteractive && {
      "&:hover": {
        transform: "translateX(2px)",
        background: hoverBg,
        borderColor: hoverBorder,
      },
    }),
  };
});

export const AccordionSessionIcon = styled("div")(
  ({ $isSim, $isCompleted }) => ({
    width: 28,
    height: 28,
    borderRadius: 7,
    flexShrink: 0,
    background: $isCompleted
      ? "rgba(23,178,106,0.14)"
      : $isSim
        ? "rgba(150,46,255,0.1)"
        : "rgba(1,236,255,0.1)",
    border: `1px solid ${
      $isCompleted
        ? "rgba(23,178,106,0.40)"
        : $isSim
          ? "rgba(150,46,255,0.25)"
          : "rgba(1,236,255,0.2)"
    }`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }),
);

export const AccordionSessionInfo = styled("div")({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 2,
});

export const AccordionSessionTitle = styled("span")(({ $isCompleted }) => ({
  fontSize: 14,
  color: $isCompleted ? "#C7F5DD" : "#D1C7FF",
  fontWeight: $isCompleted ? 500 : 400,
  lineHeight: 1.4,
}));

export const AccordionSessionSubtitle = styled("span")(({ $isCompleted }) => ({
  fontSize: 11,
  color: $isCompleted ? "#7FE5B0" : "#6B6A8A",
  fontFamily: "Poppins, sans-serif",
  lineHeight: 1.3,
}));

export const AccordionSessionStatusIcon = styled("div")(({ $status }) => ({
  width: 22,
  height: 22,
  borderRadius: "50%",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background:
    $status === "completed"
      ? "rgba(23,178,106,0.16)"
      : $status === "next"
        ? "rgba(1,236,255,0.15)"
        : "transparent",
  border:
    $status === "completed"
      ? "1px solid rgba(23,178,106,0.45)"
      : $status === "next"
        ? "1px solid rgba(1,236,255,0.35)"
        : "none",
}));

export const AccordionPillsRow = styled("div")({
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
});

// ── HoverVideoArt ──────────────────────────────────────────────────────────────

export const HoverVideoContainer = styled("div")(({ $fillHeight }) => ({
  position: "relative",
  width: "100%",
  ...($fillHeight ? { height: "100%" } : { aspectRatio: "16/10" }),
  overflow: "hidden",
  isolation: "isolate",
}));

export const HoverVideoGradientBg = styled("div")(({ $a, $b }) => ({
  position: "absolute",
  inset: 0,
  background: `radial-gradient(120% 80% at 30% 20%,${$a}55 0%,transparent 60%),radial-gradient(120% 80% at 80% 90%,${$b}66 0%,transparent 60%),linear-gradient(135deg,${$a}33 0%,${$b}55 100%)`,
}));

export const HoverVideoGrid = styled("div")({
  position: "absolute",
  inset: 0,
  backgroundImage:
    "linear-gradient(rgba(255,255,255,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.04) 1px,transparent 1px)",
  backgroundSize: "28px 28px",
  opacity: 0.5,
});

export const HoverVideoGlow = styled("div")(({ $a }) => ({
  position: "absolute",
  inset: "-20%",
  background: `radial-gradient(circle,${$a}88 0%,transparent 70%)`,
  opacity: 0.5,
  animation: "mp-pulse 4s ease-in-out infinite",
  filter: "blur(40px)",
}));

export const HoverVideoShimmer = styled("div")({
  position: "absolute",
  inset: 0,
  background:
    "linear-gradient(110deg,transparent 25%,rgba(255,255,255,0.12) 50%,transparent 75%)",
  backgroundSize: "200% 100%",
  animation: "mp-shimmer 2.4s linear infinite",
});

export const HoverVideoElement = styled("video")(({ $showVideo }) => ({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  opacity: $showVideo ? 1 : 0,
  transition: "opacity 700ms ease",
  zIndex: 2,
}));

export const HoverVideoOverlay = styled("div")(({ $showVideo }) => ({
  position: "absolute",
  inset: 0,
  background: "linear-gradient(180deg,rgba(0,0,0,0) 40%,rgba(0,0,0,0.5) 100%)",
  opacity: $showVideo ? 1 : 0,
  transition: "opacity 400ms",
  zIndex: 2,
  pointerEvents: "none",
}));

export const HoverVideoAvatarWrap = styled("div")(({ $left, $top, $size }) => ({
  position: "absolute",
  left: $left,
  top: $top,
  width: $size,
  height: $size,
  zIndex: 3,
  transition:
    "left 600ms cubic-bezier(0.16,1,0.3,1),top 600ms cubic-bezier(0.16,1,0.3,1),width 600ms cubic-bezier(0.16,1,0.3,1),height 600ms cubic-bezier(0.16,1,0.3,1)",
}));

export const HoverVideoAvatarRing = styled("div")(({ $a, $b, $isPip }) => ({
  position: "absolute",
  inset: -6,
  borderRadius: "50%",
  background: `linear-gradient(135deg,${$a},${$b})`,
  opacity: $isPip ? 0 : 0.6,
  filter: "blur(8px)",
  animation: "mp-pulse-ring 2.4s ease-out infinite",
  transition: "opacity 400ms",
  pointerEvents: "none",
}));

export const HoverVideoAvatarImg = styled("div")(({ $src, $isPip }) => ({
  width: "100%",
  height: "100%",
  borderRadius: "50%",
  backgroundImage: `url(${$src})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  boxShadow: $isPip
    ? "0 0 0 3px rgba(255,255,255,0.85),0 4px 12px rgba(0,0,0,0.4)"
    : "0 0 0 4px #FEFEFE,0 8px 24px rgba(0,0,0,0.3)",
}));

export const HoverVideoPlayBadge = styled("div")(({ $a, $b }) => ({
  position: "absolute",
  right: -4,
  bottom: -4,
  width: 30,
  height: 30,
  borderRadius: "50%",
  background: `linear-gradient(135deg,${$a},${$b})`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 0 0 3px #FEFEFE",
}));

export const HoverVideoGuruLabel = styled("div")(({ $hovered }) => ({
  position: "absolute",
  left: "50%",
  bottom: 16,
  transform: "translateX(-50%)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  transition: "opacity 300ms",
  zIndex: 3,
  opacity: $hovered ? 0 : 1,
  pointerEvents: "none",
}));

export const HoverVideoGuruSubtitle = styled("span")({
  fontSize: 11,
  color: "rgba(255,255,255,0.7)",
  fontWeight: 500,
});

export const HoverVideoGuruName = styled("span")({
  fontSize: 16,
  fontWeight: 700,
  color: "#FEFEFE",
});

export const HoverVideoStatusBadge = styled("div")({
  position: "absolute",
  bottom: 12,
  left: 12,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "5px 10px",
  borderRadius: 999,
  color: "#fff",
  fontSize: 11,
  fontWeight: 500,
  zIndex: 4,
  backdropFilter: "blur(8px)",
});

export const HoverVideoLoadingBadge = styled(HoverVideoStatusBadge)({
  background: "rgba(0,0,0,0.7)",
});

export const HoverVideoPlayingBadge = styled(HoverVideoStatusBadge)({
  background: "rgba(0,0,0,0.6)",
  "@media(max-width:639px)": {
    padding: "5px 6px",
    gap: 0,
    "& > span:last-child": { display: "none" },
  },
});

export const HoverVideoMuteBtn = styled("button")(({ $muted }) => ({
  position: "absolute",
  top: 12,
  right: 12,
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  padding: "8px 14px",
  borderRadius: 999,
  background: "rgba(0,0,0,0.7)",
  color: "#fff",
  fontSize: 13,
  fontWeight: 600,
  border: "1.5px solid rgba(255,255,255,0.25)",
  cursor: "pointer",
  zIndex: 5,
  backdropFilter: "blur(8px)",
  outline: "none",
  letterSpacing: "0.02em",
  animation: $muted ? `${mutePulse} 2s ease-in-out infinite` : "none",
  "&:hover": { background: "rgba(0,0,0,0.9)", animationPlayState: "paused" },
}));

// ── ProgramCard ────────────────────────────────────────────────────────────────

export const ProgramCardRoot = styled("article")(({ $hovered }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  background:
    "linear-gradient(180deg,rgba(33,31,69,0.7) 0%,rgba(33,31,69,0.4) 100%)",
  border: `1px solid ${$hovered ? "rgba(1,236,255,0.4)" : "rgba(255,255,255,0.08)"}`,
  borderRadius: 16,
  overflow: "hidden",
  cursor: "pointer",
  transform: $hovered ? "translateY(-6px)" : undefined,
  boxShadow: $hovered
    ? "0 20px 60px rgba(1,236,255,0.15),0 0 0 1px rgba(1,236,255,0.2)"
    : undefined,
  transition:
    "transform 350ms cubic-bezier(0.16,1,0.3,1),box-shadow 350ms,border-color 350ms",
}));

export const ProgramCardBody = styled("div")({
  padding: "20px 22px 16px",
  display: "flex",
  flexDirection: "column",
  gap: 12,
  flex: 1,
});

export const ProgramCardTitle = styled("h3")({
  fontSize: 18,
  fontWeight: 700,
  color: "#01ECFF",
  margin: 0,
  lineHeight: 1.3,
  letterSpacing: "-0.01em",
});

export const ProgramCardBlurb = styled("p")({
  fontSize: 13,
  color: "#D1C7FF",
  margin: 0,
  lineHeight: 1.5,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
});

export const ProgramCardTagsRow = styled("div")({
  display: "flex",
  gap: 6,
  flexWrap: "wrap",
});

export const ProgramCardMeta = styled("div")({
  display: "flex",
  gap: 14,
  flexWrap: "wrap",
  paddingTop: 8,
  borderTop: "1px solid rgba(255,255,255,0.06)",
});

export const ProgramCardMetaItem = styled("span")(({ $isRating }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  fontSize: 12,
  color: $isRating ? "#FEC84B" : "rgb(254, 254, 254)",
  "& svg, & svg path": {
    color: "#A87EFF",
  },
}));

export const ProgramCardActions = styled("div")({
  display: "flex",
  gap: 8,
  padding: "14px 18px",
  background: "rgba(33,31,69,0.5)",
  borderTop: "1px solid rgba(255,255,255,0.06)",
  "& > *": { flex: 1 },
});

export const ProgramCardFeaturedBadge = styled("div")({
  position: "absolute",
  top: 12,
  left: 12,
  zIndex: 5,
  padding: "4px 10px",
  borderRadius: 999,
  background: "linear-gradient(95deg,#01ECFF,#962EFF)",
  color: "#030039",
  fontSize: 11,
  fontWeight: 600,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
});

// ── SpotlightCard ──────────────────────────────────────────────────────────────

export const SpotlightCardRoot = styled("article")(({ $hovered }) => ({
  background: "linear-gradient(180deg,rgba(33,31,69,0.7),rgba(33,31,69,0.4))",
  border: `1px solid ${$hovered ? "rgba(1,236,255,0.4)" : "rgba(255,255,255,0.10)"}`,
  borderRadius: 18,
  overflow: "hidden",
  cursor: "pointer",
  transform: $hovered ? "translateY(-4px)" : undefined,
  boxShadow: $hovered ? "0 24px 60px rgba(1,236,255,0.18)" : undefined,
  transition: "all 350ms cubic-bezier(0.16,1,0.3,1)",
}));

export const SpotlightVideoWrap = styled("div")({
  position: "relative",
  overflow: "hidden",
  minHeight: 280,
});

export const SpotlightVideoInner = styled("div")({
  position: "absolute",
  inset: 0,
});

export const SpotlightBody = styled("div")({
  padding: 24,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  minWidth: 0,
});

export const SpotlightPillRow = styled("div")({
  display: "flex",
  gap: 6,
});

export const SpotlightTitle = styled("h2")({
  fontSize: 24,
  fontWeight: 700,
  color: "#01ECFF",
  margin: 0,
  lineHeight: 1.2,
});

export const SpotlightBlurb = styled("p")({
  fontSize: 14,
  color: "#D1C7FF",
  margin: 0,
  lineHeight: 1.5,
});

export const SpotlightOutcome = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 12px",
  background: "rgba(1,236,255,0.06)",
  borderRadius: 8,
  border: "1px solid rgba(1,236,255,0.18)",
  fontSize: 14,
  color: "#FEFEFE",
  fontWeight: 500,
});

export const SpotlightGuruRow = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "15px 0",
  borderTop: "1px solid rgba(255,255,255,0.06)",
  borderBottom: "1px solid rgba(255,255,255,0.06)",
});

export const SpotlightGuruText = styled("div")({
  minWidth: 0,
});

export const SpotlightGuruName = styled("div")({
  fontSize: 13,
  fontWeight: 600,
  color: "#FEFEFE",
});

export const SpotlightGuruRole = styled("div")({
  fontSize: 11,
  color: "#A87EFF",
});

export const SpotlightStats = styled("div")({
  display: "flex",
  gap: 14,
  flexWrap: "wrap",
  fontSize: 13,
  color: "rgb(254, 254, 254)",
  "& svg, & svg path": {
    color: "#A87EFF",
  },
});

export const SpotlightStatItem = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
});

export const SpotlightActions = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  marginTop: "auto",
  paddingTop: 4,
});

// ── MobileCarousel ─────────────────────────────────────────────────────────────

export const PaginationDotsRoot = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: 6,
  paddingTop: 16,
});

export const PaginationDot = styled("div")(({ $isActive }) => ({
  width: $isActive ? 20 : 6,
  height: 6,
  borderRadius: 999,
  background: $isActive ? "#01ECFF" : "rgba(255,255,255,0.28)",
  transition: "width 250ms ease, background 250ms ease",
  flexShrink: 0,
}));

export const MobileCardRoot = styled("article")({
  display: "flex",
  flexDirection: "column",
  background:
    "linear-gradient(180deg,rgba(33,31,69,0.7) 0%,rgba(33,31,69,0.4) 100%)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 16,
  overflow: "hidden",
  height: "100%",
});

export const MobileCardBody = styled("div")({
  padding: "16px 18px 0",
  display: "flex",
  flexDirection: "column",
  gap: 10,
  flex: 1,
});

export const MobileCardGuruRow = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 8,
});

export const MobileCardGuruName = styled("span")({
  fontSize: 12,
  fontWeight: 600,
  color: "#D1C7FF",
});

export const MobileCardTitle = styled("h3")({
  fontSize: 17,
  fontWeight: 700,
  color: "#01ECFF",
  margin: 0,
  lineHeight: 1.3,
});

export const MobileCardRatingRow = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 6,
});

export const MobileCardRatingText = styled("span")({
  fontSize: 12,
  color: "#A87EFF",
  fontWeight: 500,
});

export const MobileCardStats = styled("div")({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "6px 10px",
  padding: "10px 0",
  borderTop: "1px solid rgba(255,255,255,0.07)",
});

export const MobileCardStatItem = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 6,
});

export const MobileCardStatIcon = styled("span")({
  color: "#A87EFF",
  display: "flex",
});

export const MobileCardStatText = styled("span")({
  fontSize: 12,
  color: "#D1C7FF",
  lineHeight: 1.3,
  "& > strong": {
    color: "#FEFEFE",
    fontWeight: 600,
  },
});

export const MobileCardActions = styled("div")({
  display: "flex",
  gap: 8,
  padding: "12px 18px 16px",
  "& > *": { flex: 1 },
});

// ── TopicChip ──────────────────────────────────────────────────────────────────

export const TopicChipButton = styled("div")({
  padding: "9px 18px",
  borderRadius: 8,
  fontFamily: "Poppins,sans-serif",
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  color: "#D1C7FF",
  fontSize: 14,
  fontWeight: 500,
  cursor: "default",
  transition: "all 150ms",
  maxWidth: "100%",
  minWidth: 0,
  overflowWrap: "anywhere",
  "&:hover": {
    background: "rgba(150,46,255,0.14)",
    border: "1px solid rgba(150,46,255,0.45)",
    color: "#FEFEFE",
  },
});

export const TopicChipTooltipTitle = styled("p")({
  margin: "0 0 4px",
  fontSize: 13,
  fontWeight: 600,
  color: "#02002B",
  fontFamily: "Poppins,sans-serif",
});

export const TopicChipTooltipDesc = styled("p")({
  margin: 0,
  fontSize: 12,
  color: "#02002B",
  fontWeight: "400",
  lineHeight: 1.55,
  fontFamily: "Poppins,sans-serif",
});

// ── CallModal ──────────────────────────────────────────────────────────────────

export const CallModalRoot = styled("div")({
  position: "fixed",
  inset: 0,
  background: "#03003A",
  zIndex: 1400,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  animation: "mp-fade-in 200ms ease-out",
});

export const CallModalHeader = styled("div")({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "20px 28px",
  background: "linear-gradient(180deg,rgba(3,0,58,1) 0%,transparent 100%)",
  zIndex: 2,
});

export const CallModalHeaderLeft = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 10,
});

export const CallModalLiveLabel = styled("span")({
  color: "#FEFEFE",
  fontSize: 14,
  fontWeight: 600,
  fontFamily: "Poppins,sans-serif",
});

export const CallModalProgramTitle = styled("span")({
  color: "#A87EFF",
  fontSize: 13,
  fontFamily: "Poppins,sans-serif",
});

export const CallModalVideoWrap = styled("div")({
  position: "relative",
  width: "min(520px, 88vw)",
  aspectRatio: "3/4",
  borderRadius: 28,
  overflow: "hidden",
  border: "1px solid rgba(150,46,255,0.35)",
  boxShadow: "0 0 80px rgba(150,46,255,0.25), 0 0 0 1px rgba(150,46,255,0.1)",
});

export const CallModalVideoBg = styled("div")(({ $a, $b }) => ({
  position: "absolute",
  inset: 0,
  background: `radial-gradient(120% 80% at 30% 20%,${$a}44 0%,transparent 60%),radial-gradient(120% 80% at 80% 90%,${$b}44 0%,transparent 60%),linear-gradient(135deg,${$a}22 0%,${$b}33 100%)`,
}));

export const CallModalVideoEl = styled("video")({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

export const CallModalVideoGradient = styled("div")({
  position: "absolute",
  inset: 0,
  background: "linear-gradient(180deg,transparent 50%,rgba(3,0,58,0.85) 100%)",
});

export const CallModalGuruInfo = styled("div")({
  position: "absolute",
  bottom: 28,
  left: 0,
  right: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 4,
});

export const CallModalGuruName = styled("span")({
  color: "#FEFEFE",
  fontWeight: 700,
  fontSize: 18,
  fontFamily: "Poppins,sans-serif",
});

export const CallModalGuruRole = styled("span")({
  color: "#A87EFF",
  fontSize: 12,
  fontFamily: "Poppins,sans-serif",
});

export const CallModalControls = styled("div")({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  padding: "0 0 32px",
  background: "linear-gradient(0deg,rgba(3,0,58,1) 0%,transparent 100%)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 20,
  zIndex: 2,
});

export const CallModalInputRow = styled("div")({
  width: "min(560px,90vw)",
  display: "flex",
  gap: 10,
  padding: "10px 16px",
  borderRadius: 999,
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(150,46,255,0.3)",
  backdropFilter: "blur(12px)",
});

export const CallModalInput = styled("input")({
  flex: 1,
  background: "none",
  border: "none",
  color: "#FEFEFE",
  fontSize: 14,
  outline: "none",
  fontFamily: "Poppins,sans-serif",
});

export const CallModalSendBtn = styled("button")({
  background: "none",
  border: "none",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
});

export const CallModalEndWrap = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
});

export const CallModalEndBtn = styled("button")({
  width: 64,
  height: 64,
  borderRadius: "50%",
  background: "#FF3B5B",
  border: "none",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 0 32px rgba(255,59,91,0.45)",
  transition: "transform 150ms,filter 150ms",
  "&:hover": {
    transform: "scale(1.05)",
    filter: "brightness(1.1)",
  },
});

export const CallModalEndLabel = styled("span")({
  fontSize: 12,
  color: "#A87EFF",
  fontFamily: "Poppins,sans-serif",
});

// ── PreviewModal ───────────────────────────────────────────────────────────────

export const PreviewModalBackdrop = styled("div")({
  position: "fixed",
  inset: 0,
  background: "rgba(2,0,40,0.78)",
  backdropFilter: "blur(8px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1300,
  padding: 24,
  animation: "mp-fade-in 200ms ease-out",
});

export const PreviewModalPanel = styled("div")({
  position: "relative",
  background: "#0A0754",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 20,
  width: "100%",
  maxWidth: 880,
  overflow: "hidden",
  maxHeight: "92vh",
  overflowY: "auto",
  animation: "mp-slide-up 350ms cubic-bezier(0.16,1,0.3,1)",
});

export const PreviewModalCloseBtn = styled("button")({
  position: "absolute",
  top: 16,
  right: 16,
  zIndex: 5,
  width: 36,
  height: 36,
  borderRadius: "50%",
  background: "rgba(0,0,0,0.5)",
  border: "1px solid rgba(255,255,255,0.2)",
  color: "#fff",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backdropFilter: "blur(8px)",
  fontFamily: "Poppins,sans-serif",
});

export const PreviewModalVideoWrap = styled("div")(({ $a, $b }) => ({
  position: "relative",
  aspectRatio: "16/9",
  overflow: "hidden",
  background: `linear-gradient(135deg,${$a}33,${$b}55)`,
}));

export const PreviewModalVideoEl = styled("video")({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

export const PreviewModalVideoGradient = styled("div")({
  position: "absolute",
  inset: 0,
  background: "linear-gradient(180deg,transparent 60%,rgba(10,7,84,0.8) 100%)",
  pointerEvents: "none",
});

export const PreviewModalVideoControls = styled("div")({
  position: "absolute",
  bottom: 16,
  right: 16,
  display: "flex",
  gap: 8,
});

export const PreviewModalLiveBadge = styled("div")({
  position: "absolute",
  top: 16,
  left: 16,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 12px",
  background: "rgba(0,0,0,0.6)",
  borderRadius: 999,
  color: "#fff",
  fontSize: 12,
  backdropFilter: "blur(8px)",
});

export const PreviewModalBody = styled("div")({
  padding: "28px 32px 32px",
});

export const PreviewModalPillRow = styled("div")({
  display: "flex",
  gap: 8,
  marginBottom: 12,
  flexWrap: "wrap",
});

export const PreviewModalTitle = styled("h2")({
  fontSize: 28,
  fontWeight: 700,
  color: "#FEFEFE",
  margin: "0 0 8px",
  letterSpacing: "-0.01em",
});

export const PreviewModalDesc = styled("p")({
  color: "#D1C7FF",
  fontSize: 14,
  lineHeight: 1.6,
  margin: "0 0 24px",
});

export const PreviewModalStatsGrid = styled("div")({
  display: "grid",
  gridTemplateColumns: "repeat(4,1fr)",
  gap: 16,
  padding: 16,
  borderRadius: 12,
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.08)",
  marginBottom: 24,
});

export const PreviewModalStatItem = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 2,
});

export const PreviewModalStatNum = styled("span")({
  fontSize: 18,
  fontWeight: 700,
  color: "#FEFEFE",
});

export const PreviewModalStatLabel = styled("span")({
  fontSize: 11,
  color: "#A87EFF",
});

export const PreviewModalActionRow = styled("div")({
  display: "flex",
  gap: 12,
  flexWrap: "wrap",
});

export const PreviewModalFootnote = styled("p")({
  fontSize: 12,
  color: "#A87EFF",
  margin: "14px 0 0",
});

// ── Skills Academy ────────────────────────────────────────────────────────────────

export const SkillsAcademyStatItem = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  fontSize: 12,
  color: theme.palette.text.brand_secondary,
}));

export const SkillsAcademyStatNum = styled("span")(({ theme }) => ({
  fontSize: 22,
  fontWeight: 700,
  lineHeight: 1,
  color: theme.palette.text.primary,
}));

export const SkillsAcademySearchBox = styled("div")(
  ({ theme, $searchFocused }) => ({
    flex: 1,
    minWidth: 280,
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "0 14px",
    height: 50,
    borderRadius: 10,
    background: theme.palette.background.primary,
    border: $searchFocused
      ? "1px solid rgba(150,46,255,0.6)"
      : "1px solid rgba(255,255,255,0.10)",
    boxShadow: $searchFocused ? "0 0 0 3px rgba(150,46,255,0.14)" : "none",
    transition: "border-color 150ms, box-shadow 150ms",
  }),
);

export const SkillsAcademySearchInput = styled("input")(({ theme }) => ({
  flex: 1,
  background: "none",
  border: "none",
  fontSize: 14,
  outline: "none",
  fontFamily: "Poppins,sans-serif",
  color: theme.palette.text.primary,
}));

export const SkillsAcademyFilterRow = styled("div")({
  display: "flex",
  gap: 12,
  flexWrap: "wrap",
});

export const SkillsAcademyChipButton = styled("button")(({ $isSelected }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 14px",
  borderRadius: 999,
  background: $isSelected
    ? "linear-gradient(95deg,rgba(1,236,255,0.18),rgba(150,46,255,0.18))"
    : "transparent",
  border: $isSelected
    ? "1px solid rgba(150,46,255,0.6)"
    : "1px solid rgba(255,255,255,0.12)",
  color: $isSelected ? "#FEFEFE" : "#D1C7FF",
  fontSize: 13,
  fontWeight: $isSelected ? 600 : 500,
  cursor: "pointer",
  transition: "all 150ms",
  fontFamily: "Poppins,sans-serif",
  flexShrink: 0,
}));

export const SkillsAcademyChipX = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 14,
  height: 14,
  borderRadius: "50%",
  background: "rgba(255,255,255,0.15)",
  fontSize: 9,
  fontWeight: 700,
  lineHeight: 1,
});

export const SkillsAcademyResultsText = styled("p")(({ theme }) => ({
  padding: "12px 0",
  fontSize: 14,
  margin: 0,
  color: theme.palette.text.brand_secondary,
}));

export const SkillsAcademyResultsStrong = styled("strong")(({ theme }) => ({
  color: theme.palette.text.primary,
}));

export const SkillsAcademyResultsEm = styled("em")({
  color: "#01ECFF",
  fontStyle: "normal",
});

export const SkillsAcademySpotlightSection = styled("section")({
  padding: "12px 0 28px",
});

export const SkillsAcademyEmptyState = styled("div")({
  textAlign: "center",
  padding: "60px 0",
  color: "#D1C7FF",
});

export const SkillsAcademyEmptyTitle = styled("h3")(({ theme }) => ({
  margin: "16px 0 4px",
  fontSize: 18,
  color: theme.palette.text.primary,
}));

// ── ProgramInfoPage ────────────────────────────────────────────────────────────

export const InfoBackButton = styled("button")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  background: "none",
  border: "none",
  fontSize: 14,
  cursor: "pointer",
  fontFamily: "Poppins,sans-serif",
  fontWeight: 500,
  color: theme.palette.text.brand_secondary,
  transition: "color 150ms",
  "&:hover": {
    color: "#FEFEFE",
  },
}));

export const InfoBackWrap = styled("div")({});

export const InfoHeroSection = styled("section")(({ $bg }) => ({
  background: $bg,
}));

export const InfoHeroOverlay = styled("div")({
  position: "absolute",
  inset: 0,
  background:
    "radial-gradient(circle at 30% 20%,rgba(255,255,255,0.15),transparent 60%),radial-gradient(circle at 80% 80%,rgba(0,0,0,0.2),transparent 60%)",
});

export const InfoVideoBox = styled("div")({
  position: "relative",
  aspectRatio: "16/10",
  borderRadius: 14,
  overflow: "hidden",
  background: "#000",
  boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
  border: "1px solid #211F45",
});

export const InfoVideoImg = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  animation: "mp-zoom 8s ease-in-out infinite alternate",
});

export const InfoVideoShimmer = styled("div")({
  position: "absolute",
  inset: 0,
  background:
    "linear-gradient(110deg,transparent 30%,rgba(255,255,255,0.08) 50%,transparent 70%)",
  backgroundSize: "200% 100%",
  animation: "mp-shimmer 4s linear infinite",
});

export const InfoChatPill = styled("div")({
  position: "absolute",
  top: 12,
  left: 12,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  padding: "6px 14px",
  background: "rgba(33,31,69,0.85)",
  border: "1px solid rgba(150,46,255,0.55)",
  backdropFilter: "blur(12px)",
  color: "#FEFEFE",
  fontSize: 12,
  fontWeight: 600,
  borderRadius: 999,
});

export const InfoPlayBtn = styled("button")({
  position: "absolute",
  left: "50%",
  top: "50%",
  transform: "translate(-50%,-50%)",
  width: 80,
  height: 80,
  borderRadius: "50%",
  background: "rgba(255,255,255,0.2)",
  border: "2px solid rgba(255,255,255,0.5)",
  backdropFilter: "blur(12px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
});

export const InfoColumn = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 16,
});

export const InfoPillRow = styled("div")({
  display: "flex",
  gap: 8,
});

export const InfoHeroTitle = styled("h1")({
  fontWeight: 700,
  color: "#FEFEFE",
  margin: 0,
  lineHeight: 1.1,
  letterSpacing: "-0.02em",
});

export const InfoHeroBlurb = styled("p")({
  fontSize: 16,
  color: "rgba(255,255,255,0.9)",
  margin: 0,
  lineHeight: 1.5,
  "@media(max-width:639px)": {
    fontSize: 14,
  },
});

export const InfoGuruCard = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "14px 16px",
  borderRadius: 12,
  background: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(255,255,255,0.18)",
  backdropFilter: "blur(8px)",
});

export const InfoGuruName = styled("div")({
  fontSize: 14,
  fontWeight: 600,
  color: "#FEFEFE",
});

export const InfoGuruRole = styled("div")({
  fontSize: 12,
  color: "rgba(255,255,255,0.7)",
});

export const InfoStatsRow = styled("div")({
  display: "flex",
  gap: 20,
  flexWrap: "wrap",
  padding: "12px 0",
  borderTop: "1px solid rgba(255,255,255,0.18)",
  borderBottom: "1px solid rgba(255,255,255,0.18)",
});

export const InfoStatItem = styled("div")({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  color: "rgba(255,255,255,0.85)",
  fontSize: 13,
});

export const InfoActionsRow = styled("div")({
  display: "flex",
  gap: 10,
  alignItems: "center",
  flexWrap: "wrap",
});

export const InfoOutcomeBanner = styled("section")({
  padding: "24px 0 0",
});

export const InfoOutcomeBox = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 16,
  padding: "20px 28px",
  background: "linear-gradient(95deg,rgba(1,236,255,0.1),rgba(150,46,255,0.1))",
  border: "1px solid rgba(1,236,255,0.25)",
  borderRadius: 14,
});

export const InfoOutcomeTitle = styled("div")({
  fontSize: 18,
  fontWeight: 700,
  color: "#FEFEFE",
});

export const InfoTabSection = styled("section")({
  paddingTop: 32,
});

export const InfoOverviewGrid = styled("div")({
  display: "grid",
  gap: 20,
  gridTemplateColumns: "repeat(auto-fit,minmax(min(280px,100%),1fr))",
});

export const InfoLearnCard = styled("div")({
  padding: "24px 28px",
  borderRadius: 16,
  background: "rgba(1,236,255,0.04)",
  border: "1px solid rgba(1,236,255,0.15)",
  display: "flex",
  flexDirection: "column",
  gap: 18,
});

export const InfoLearnCardHeader = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 10,
});

export const InfoLearnIconBox = styled("div")({
  width: 36,
  height: 36,
  borderRadius: 10,
  background: "rgba(1,236,255,0.1)",
  border: "1px solid rgba(1,236,255,0.25)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

export const InfoLearnTitle = styled("h3")({
  margin: 0,
  fontSize: 16,
  fontWeight: 700,
  color: "#01ECFF",
});

export const InfoLearnGrid = styled("div")({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "10px 16px",
  "@media(max-width:639px)": {
    gridTemplateColumns: "1fr",
  },
});

export const InfoLearnItem = styled("div")({
  display: "flex",
  alignItems: "flex-start",
  gap: 10,
});

export const InfoLearnItemText = styled("span")({
  fontSize: 14,
  color: "#D1C7FF",
  lineHeight: 1.45,
});

export const InfoTopicsCard = styled("div")({
  padding: "24px 28px",
  borderRadius: 16,
  background: "rgba(150,46,255,0.04)",
  border: "1px solid rgba(150,46,255,0.25)",
  display: "flex",
  flexDirection: "column",
  gap: 18,
});

export const InfoTopicsCardHeader = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 10,
});

export const InfoTopicsIconBox = styled("div")({
  width: 36,
  height: 36,
  borderRadius: 10,
  background: "rgba(150,46,255,0.1)",
  border: "1px solid rgba(150,46,255,0.25)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

export const InfoTopicsTitle = styled("h3")({
  margin: 0,
  fontSize: 16,
  fontWeight: 700,
  color: "#A87EFF",
});

export const InfoTopicsChipWrap = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  gap: 10,
});

export const InfoAboutCard = styled("div")({
  padding: "24px 28px",
  borderRadius: 16,
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,255,255,0.08)",
  display: "flex",
  flexDirection: "column",
  gap: 14,
});

export const InfoAboutTitle = styled("h3")({
  margin: 0,
  fontSize: 16,
  fontWeight: 700,
  color: "#A87EFF",
});

export const InfoAboutText = styled("p")({
  margin: 0,
  fontSize: 15,
  color: "#D1C7FF",
  lineHeight: 1.75,
});

export const InfoTopicsTabWrap = styled("div")({
  marginBottom: 24,
});

export const InfoTopicsTabTitle = styled("h2")(({ theme }) => ({
  fontSize: 22,
  fontWeight: 700,
  margin: "0 0 6px",
  color: theme.palette.text.primary,
}));

export const InfoTopicsTabDesc = styled("p")(({ theme }) => ({
  fontSize: 14,
  margin: 0,
  color: theme.palette.text.brand_secondary,
}));

export const InfoAccordionWrap = styled("div")({
  padding: "8px 24px 0",
  borderRadius: 16,
  background: "rgba(255,255,255,0.02)",
  border: "1px solid rgba(255,255,255,0.07)",
});

export const InfoRatingSidebar = styled("div")({
  padding: "24px 20px",
  borderRadius: 16,
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,255,255,0.08)",
  display: "flex",
  flexDirection: "column",
  gap: 20,
});

export const InfoRatingBig = styled("div")({
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
});

export const InfoRatingNumber = styled("div")({
  fontSize: 56,
  fontWeight: 700,
  color: "#FEFEFE",
  lineHeight: 1,
});

export const InfoRatingCount = styled("div")({
  fontSize: 13,
  color: "#A87EFF",
  marginTop: 2,
});

export const InfoDivider = styled("div")({
  height: 1,
  background: "rgba(255,255,255,0.07)",
});

export const InfoBreakdownList = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 8,
});

export const InfoBreakdownRow = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 10,
});

export const InfoBreakdownStar = styled("span")({
  fontSize: 13,
  color: "#D1C7FF",
  width: 18,
  textAlign: "right",
});

export const InfoBreakdownBarWrap = styled("div")({
  flex: 1,
  height: 7,
  background: "rgba(255,255,255,0.07)",
  borderRadius: 999,
  overflow: "hidden",
});

export const InfoBreakdownBar = styled("div")(({ $pct }) => ({
  height: "100%",
  width: `${$pct}%`,
  background:
    $pct > 0 ? "linear-gradient(90deg,#01ECFF,#962EFF)" : "transparent",
  borderRadius: 999,
  transition: "width 400ms ease",
}));

export const InfoBreakdownPct = styled("span")({
  fontSize: 12,
  color: "#A87EFF",
  width: 30,
  textAlign: "right",
});

export const InfoMentionsLabel = styled("div")({
  fontSize: 11,
  fontWeight: 600,
  color: "#A87EFF",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  marginBottom: 12,
});

export const InfoMentionsRow = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
});

export const InfoMentionChip = styled("div")({
  padding: "6px 12px",
  borderRadius: 20,
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.12)",
  fontSize: 12,
  color: "#D1C7FF",
  fontFamily: "Poppins,sans-serif",
});

export const InfoReviewList = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 14,
  minWidth: 0,
});

export const InfoReviewCard = styled("div")({
  padding: "20px 24px",
  borderRadius: 16,
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,255,255,0.08)",
  minWidth: 0,
});

export const InfoReviewHeader = styled("div")({
  display: "flex",
  alignItems: "flex-start",
  gap: 14,
  marginBottom: 14,
});

export const InfoReviewInitials = styled("div")({
  width: 44,
  height: 44,
  borderRadius: "50%",
  flexShrink: 0,
  background: "linear-gradient(135deg,#01ECFF,#0ABFCF)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 14,
  fontWeight: 700,
  color: "#0A0A2E",
  fontFamily: "Poppins,sans-serif",
});

export const InfoReviewMeta = styled("div")({
  flex: 1,
  minWidth: 0,
});

export const InfoReviewMetaTop = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "wrap",
  marginBottom: 4,
});

export const InfoReviewerName = styled("span")({
  fontSize: 15,
  fontWeight: 700,
  color: "#A87EFF",
  fontFamily: "Poppins,sans-serif",
  "@media(max-width:639px)": {
    width: "100%",
  },
});

export const InfoReviewBadgeGroup = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "nowrap",
});

export const InfoReviewVerifiedBadge = styled("div")({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "3px 8px",
  borderRadius: 20,
  background: "rgba(23,178,106,0.12)",
  border: "1px solid rgba(23,178,106,0.3)",
  fontSize: 11,
  color: "#17B26A",
  fontFamily: "Poppins,sans-serif",
  fontWeight: 500,
});

export const InfoReviewCompletedBadge = styled("div")({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "3px 8px",
  borderRadius: 20,
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.14)",
  fontSize: 11,
  color: "#D1C7FF",
  fontFamily: "Poppins,sans-serif",
  fontWeight: 500,
});

export const InfoReviewTime = styled("div")({
  fontSize: 12,
  color: "#6B6A8A",
  fontFamily: "Poppins,sans-serif",
});

export const InfoReviewText = styled("p")({
  margin: 0,
  fontSize: 14,
  color: "#D1C7FF",
  lineHeight: 1.65,
  fontFamily: "Poppins,sans-serif",
});

export const InfoLoadMoreBtn = styled("button")({
  width: "100%",
  padding: "16px",
  borderRadius: 14,
  background: "transparent",
  border: "1px solid rgba(150,46,255,0.35)",
  color: "#FEFEFE",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "Poppins,sans-serif",
  transition: "border-color 150ms, background 150ms",
  "&:hover": {
    borderColor: "rgba(150,46,255,0.7)",
    background: "rgba(150,46,255,0.06)",
  },
});

export const InfoGuruImageWrap = styled("div")({
  position: "relative",
  borderRadius: 20,
  overflow: "hidden",
  aspectRatio: "1/1",
  background: "#211F45",
});

export const InfoGuruImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

export const InfoGuruImageOverlay = styled("div")(({ $b }) => ({
  position: "absolute",
  inset: 0,
  background: `linear-gradient(180deg,transparent 50%,rgba(3,0,57,0.7) 100%)`,
}));

export const InfoCredentialsList = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 10,
});

export const InfoCredentialsLabel = styled("div")(({ theme }) => ({
  fontSize: 12,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  fontWeight: 500,
  color: theme.palette.text.brand_secondary,
}));

export const InfoCredentialItem = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 14px",
  borderRadius: 10,
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,255,255,0.07)",
});

export const InfoCredentialText = styled("span")({
  fontSize: 13,
  color: "#D1C7FF",
});

export const InfoGuroBioCol = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 20,
  paddingTop: 8,
});

export const InfoGuroBioName = styled("h2")({
  fontSize: 28,
  fontWeight: 700,
  color: "#FEFEFE",
  margin: "0 0 6px",
});

export const InfoGuroBioRole = styled("p")({
  fontSize: 14,
  color: "#A87EFF",
  margin: 0,
});

export const InfoGuroBioPara = styled("p")({
  fontSize: 15,
  color: "#D1C7FF",
  lineHeight: 1.7,
  margin: 0,
});

export const InfoFinalCTA = styled("section")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "28px 32px",
  marginTop: 40,
  borderRadius: 16,
  background:
    "linear-gradient(95deg,rgba(1,236,255,0.08) 0%,rgba(150,46,255,0.10) 100%)",
  border: "1px solid rgba(1,236,255,0.2)",
  flexWrap: "wrap",
  gap: 16,
});

export const InfoFinalCTATitle = styled("h3")({
  fontSize: 22,
  fontWeight: 700,
  color: "#FEFEFE",
  margin: "0 0 4px",
});

export const InfoFinalCTASubtitle = styled("p")({
  color: "#A87EFF",
  fontSize: 14,
  margin: 0,
});

export const InfoFinalCTAActions = styled("div")({
  display: "flex",
  gap: 12,
});

export const InfoOverviewTabWrap = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 20,
});

export const InfoOutcomeLearners = styled("div")(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.text.brand_secondary,
}));

export const InfoStatStrong = styled("strong")({
  color: "#FEFEFE",
  fontWeight: 600,
});

export const InfoGuroBioStartWrap = styled("div")({
  alignSelf: "flex-start",
  "@media(max-width:639px)": { display: "none" },
  marginTop: "20px",
});

export const InfoGuruLeftCol = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 20,
});

// Inline SVG used as a check icon in the learn checklist
export const InfoLearnCheckSvg = styled("svg")({
  flexShrink: 0,
  marginTop: 1,
});

// IconBtn variant — transparent ghost with white border (used in hero + modals)
export const IconBtnGhostWhite = styled(StyledIconBtn)({
  background: "transparent",
  border: "1px solid rgba(255,255,255,0.2)",
  color: "#fff",
});

// IconBtn variant — no background, no border (used as clear/dismiss button)
export const IconBtnClear = styled(StyledIconBtn)({
  background: "transparent",
  border: "none",
});
